'use client';

import React, { Suspense, useEffect, useState } from 'react';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import AppLogo from '@/components/ui/AppLogo';
import { API_LOCATION_LABEL, errorMessage } from '@/lib/api/client';
import { Eye, EyeOff, LogIn, AlertCircle, Clock } from 'lucide-react';

/**
 * The reasons a session can end, in the operator's terms.
 *
 * A closed set, keyed on the `reason` parameter: anything else is ignored
 * rather than rendered, so a hand-typed or stale URL cannot put arbitrary text
 * on the sign-in page.
 */
const REASONS: Record<string, string> = {
  expired:
    'Your session ended, so you were signed out. Sign in again and you will be returned to the page you were on.',
  signed_out: 'You have been signed out.',
  unauthorized: 'That page needs a session the CMS could not confirm. Sign in again to continue.',
};

function LoginForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { signIn, user, loading: authLoading } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const nextPath = searchParams.get('next') || '/';
  /**
   * Why this page is being shown.
   *
   * An operator who is bounced here mid-task has done nothing wrong, and a bare
   * sign-in form implies they did — or that they were signed out deliberately.
   * `reason` is set by `SessionWatcher` (expiry) and by the sign-out flow, and
   * is the difference between "log in again" and "what just happened?".
   */
  const reason = searchParams.get('reason');

  /**
   * The one place this page navigates away from itself.
   *
   * `handleSubmit` used to call `router.replace(nextPath)` as well, immediately
   * after `signIn` — and since `signIn` sets the user, this effect fired for the
   * same destination in the same tick. Two concurrent replaces to one route can
   * cancel each other in the App Router, and the observed result was a page that
   * stayed on `/login` rendering nothing at all (the `if (user) return null`
   * below), with a valid session and no way forward but a manual reload. Having a
   * single owner of the redirect removes the race rather than papering over it.
   */
  useEffect(() => {
    if (!authLoading && user) router.replace(nextPath);
  }, [user, authLoading, router, nextPath]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!email || !password) {
      setError('Email and password are required.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      // Navigation is the effect above's job — see the note there.
      await signIn(email.trim(), password);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: 'var(--background)' }}
      >
        <span className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (user) return null;

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={{ background: 'var(--background)' }}
    >
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center mb-8">
          <div className="flex items-center gap-3 mb-3">
            <AppLogo size={36} />
            <span className="text-xl font-700 text-foreground tracking-tight">PersonaMatrix</span>
          </div>
          <p className="text-xs text-muted-foreground text-center">
            Character Intelligence CMS · Admin Portal
          </p>
        </div>

        <div
          className="rounded-lg p-6 border"
          style={{ background: 'var(--surface-elevated)', borderColor: 'var(--border)' }}
        >
          <h1 className="text-lg font-700 text-foreground mb-1">Sign In</h1>
          <p className="text-xs text-muted-foreground mb-5">
            Authenticated against Supabase through the Admin API.
          </p>

          {/* Why the operator is here, when they did not come here on purpose.
              Shown above the credential error, and only when there is no
              credential error to show -- a failed sign-in is the more recent
              and more actionable of the two. */}
          {!error && reason && REASONS[reason] && (
            <div
              className="flex items-start gap-2 p-3 rounded mb-4 text-xs"
              style={{
                background: 'rgba(250,204,21,0.08)',
                border: '1px solid rgba(250,204,21,0.3)',
                color: '#fbbf24',
              }}
              role="status"
              data-testid="login-reason"
            >
              <Clock size={14} className="flex-shrink-0 mt-0.5" />
              <span>{REASONS[reason]}</span>
            </div>
          )}

          {error && (
            <div
              className="flex items-start gap-2 p-3 rounded mb-4 text-xs"
              style={{
                background: 'rgba(251,113,133,0.08)',
                border: '1px solid rgba(251,113,133,0.25)',
                color: '#fb7185',
              }}
              role="alert"
            >
              <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-600 text-foreground mb-1.5">Email Address</label>
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="admin@example.com"
                autoComplete="email"
                className="input-field w-full px-3 py-2.5 rounded text-sm"
                disabled={loading}
              />
            </div>

            <div>
              <label className="block text-xs font-600 text-foreground mb-1.5">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  className="input-field w-full px-3 py-2.5 pr-10 rounded text-sm"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full flex items-center justify-center gap-2 py-2.5 rounded text-sm font-600 disabled:opacity-60 mt-2"
            >
              {loading ? (
                <>
                  <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  Signing in…
                </>
              ) : (
                <>
                  <LogIn size={15} />
                  Sign In
                </>
              )}
            </button>
          </form>
        </div>

        <p className="text-center text-2xs text-muted-foreground mt-5">
          Need an admin account?{' '}
          <Link href="/register" className="text-primary hover:underline">
            Register
          </Link>
        </p>
        <p className="text-center text-2xs text-muted-foreground/60 mt-2 font-mono break-all">
          API: {API_LOCATION_LABEL}
        </p>
      </div>
    </div>
  );
}

export default function LoginPage() {
  // useSearchParams needs a Suspense boundary in the app router.
  return (
    <Suspense
      fallback={
        <div
          className="min-h-screen flex items-center justify-center"
          style={{ background: 'var(--background)' }}
        >
          <span className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      }
    >
      <LoginForm />
    </Suspense>
  );
}
